import { type ColumnDef } from '@tanstack/react-table';
import { useAddOnAssignedProductsQuery } from '../hooks/useAddOnAssignedProductsQuery';
import { Badge, Table, TextLink } from '@shared/components';
import type { AssignedProductRecord } from '../services/productAddons';

interface Props {
    addOnId: string;
}

const columns: ColumnDef<AssignedProductRecord>[] = [
    {
        accessorKey: 'name',
        header: 'Product',
        cell: (info) => (
            <span className="font-semibold text-sm">
                <TextLink to={`/smr-admin/products/${info.row.original.id}`} color="primary">
                    {info.row.original.name}
                </TextLink>
            </span>
        ),
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) =>
            info.row.original.status === 'active'
                ? <Badge variant="success">Active</Badge>
                : <Badge variant="outline">{info.row.original.status ?? '—'}</Badge>,
    },
    {
        accessorKey: 'effective_price',
        header: () => <span className="font-mono">Effective Net</span>,
        cell: (info) => <span className="font-mono text-sm">£{info.row.original.effective_price}</span>,
    },
    {
        accessorKey: 'effective_gross_price',
        header: () => <span className="font-mono">Effective Gross</span>,
        cell: (info) => <span className="font-mono text-sm">£{info.row.original.effective_gross_price}</span>,
    },
];

export function AssignedProductsPanel({ addOnId }: Props) {
    const { data = [], isLoading } = useAddOnAssignedProductsQuery(addOnId);

    if (isLoading) return <p className="text-sm text-mp-muted">Loading assigned products…</p>;

    return (
        <div data-testid="assigned-products-panel" className="space-y-2">
            <h2 className="text-lg font-semibold">Assigned Products</h2>
            {data.length === 0 ? (
                <p className="text-sm text-mp-muted">No products currently use this optional extra.</p>
            ) : (
                <Table<AssignedProductRecord> data={data} columns={columns} enableSorting={false} getRowId={(row) => row.id} />
            )}
        </div>
    );
}
